import { useEffect, useState } from 'react';

const loadedScriptsCache = new Set<string>();

function createScriptElement(scriptUrl: string) {
  const scriptElement = document.createElement('script');

  scriptElement.src = scriptUrl;
  scriptElement.type = 'text/javascript';
  scriptElement.async = true;

  return scriptElement;
}

/**
 * Hook to dynamically load remote JavaScript scripts, specifically designed for federated modules.
 * It manages the loading state and error handling of remote scripts, with built-in caching to prevent duplicate loads.
 *
 * @param {string} remoteUrl - The base URL of the remote application where the script is hosted
 * @returns {Object} An object containing the loading state and error state
 * @returns {boolean} returns.isScriptReadyToUse - Indicates if the script is loaded and ready to use
 * @returns {boolean} returns.hasErrorWithScript - Indicates if there was an error loading the script
 *
 * @example
 * const { isScriptReadyToUse, hasErrorWithScript } = useDynamicScript('https://my-remote-app.com');
 * if (isScriptReadyToUse) {
 *   // Script is loaded and ready to use
 * }
 */
export function useDynamicScript(remoteUrl: string) {
  const [isScriptReadyToUse, setReadyToUse] = useState(false);
  const [hasErrorWithScript, setErrorWithScript] = useState(false);
  const scriptUrl = `${remoteUrl}/remoteEntry.js`;

  useEffect(() => {
    if (!scriptUrl) {
      return;
    }

    if (loadedScriptsCache.has(scriptUrl)) {
      setReadyToUse(true);
      setErrorWithScript(false);
      return;
    }

    setReadyToUse(false);
    setErrorWithScript(false);

    const newScriptElement = createScriptElement(scriptUrl);

    newScriptElement.onload = () => {
      loadedScriptsCache.add(scriptUrl);
      setReadyToUse(true);
    };

    newScriptElement.onerror = () => {
      setReadyToUse(false);
      setErrorWithScript(true);
    };

    document.head.appendChild(newScriptElement);
  }, [scriptUrl]);

  return { isScriptReadyToUse, hasErrorWithScript };
}
